import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Brand, isRemoteDataLoading } from '@common/util-models';
import { filterNullUndefined } from '@domgen/dgx-fe-common';
import {
  DynamicFormbuilderService,
  FieldDef,
  formValue,
  OptionItem,
} from '@domgen/dgx-fe-dynamic-form-builder';
import { ComponentStore } from '@ngrx/component-store';
import {
  BrandsState,
  DeviceCategory,
  DeviceCategoryType,
  DeviceDetailsField,
  DeviceFormValue,
  initialBrandState,
} from '@sky/util-models';
import { combineLatest, Observable, of } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  pluck,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { DeviceFormConfigService } from './device-form-config.service';

export interface DeviceFormDataState {
  brands: BrandsState;
  category: string;
  deviceTypes: DeviceCategoryType[];
  form?: UntypedFormGroup;
  formBuilderConfig?: FieldDef[];
  initialForm?: DeviceFormValue;
}

export interface ViewModel {
  disableBrands: boolean;
  form: UntypedFormGroup;
  formBuilderConfig: FieldDef[];
}

@Injectable()
export class DeviceFormStateService extends ComponentStore<DeviceFormDataState> {
  deviceCategoryEnum = DeviceCategory;
  deviceDetailsField = DeviceDetailsField;

  // Selectors
  readonly formBuilderConfig$: Observable<FieldDef[]> = this.select(
    (state: DeviceFormDataState) => state.formBuilderConfig
  ).pipe(filterNullUndefined());

  readonly form$: Observable<UntypedFormGroup> = this.select(
    (state: DeviceFormDataState) => state.form
  ).pipe(filterNullUndefined());

  readonly initialForm$: Observable<DeviceFormValue | undefined> = this.select(
    (state: DeviceFormDataState) => state.initialForm
  );

  readonly category$: Observable<string> = this.select(
    (state: DeviceFormDataState) => state.category
  );

  readonly deviceTypes$: Observable<DeviceCategoryType[]> = this.select(
    (state: DeviceFormDataState) => state.deviceTypes
  );

  readonly deviceTypesFormOptions$: Observable<OptionItem[]> = this.select(
    this.deviceTypes$,
    (devices: DeviceCategoryType[]) =>
      devices.map((deviceType) => ({
        label: deviceType.name,
        value: deviceType.code,
      }))
  );

  readonly brands$: Observable<BrandsState> = this.select(
    (state: DeviceFormDataState) => state.brands
  );

  readonly formValue$: Observable<DeviceFormValue> = this.form$.pipe(
    formValue()
  );

  readonly deviceTypeValue$: Observable<string | undefined> =
    this.formValue$.pipe(pluck('deviceType'), distinctUntilChanged());

  readonly brandsFormOptions$: Observable<OptionItem[]> = this.select(
    this.brands$,
    (brandsState: BrandsState) => {
      return brandsState.brands.map((brand: Brand) => ({
        label: brand.description,
        value: brand.code,
      }));
    }
  );

  readonly loadingBrandSelection$: Observable<boolean> = this.select(
    this.brands$,
    (brandsState: BrandsState) => {
      return isRemoteDataLoading(brandsState.remoteState);
    }
  );

  readonly disableBrands$: Observable<boolean> = this.select(
    this.brands$,
    (brandsState: BrandsState) => {
      return !brandsState.brands.length;
    }
  );

  readonly resetBrand = this.effect(
    (deviceTypeValue$: Observable<string | undefined>) =>
      deviceTypeValue$.pipe(
        withLatestFrom(this.form$),
        tap(([, form]) => {
          const brandControl = form.get(this.deviceDetailsField.Brand);
          brandControl?.reset('');
          brandControl?.markAsUntouched();
        })
      )
  )(this.deviceTypeValue$);

  /**
   * Updater Sources
   */
  private formBuilderConfigUpdater$ = combineLatest([
    this.category$,
    this.initialForm$,
  ]).pipe(
    map(([category, initialForm]) =>
      this.deviceFormConfigService.getFormbuilderConfig(
        category,
        this.deviceTypesFormOptions$,
        this.brandsFormOptions$,
        this.loadingBrandSelection$,
        this.disableBrands$,
        initialForm
      )
    )
  );

  private formUpdater$ = this.formBuilderConfig$.pipe(
    switchMap((formBuilderConfig) =>
      of(formBuilderConfig).pipe(
        map((conf) => this.dynamicFormBuilder.generateFormGroup(conf))
      )
    )
  );

  // ***** Updaters *****
  private readonly formBuilderConfigUpdater = this.updater(
    (state: DeviceFormDataState, formBuilderConfig: FieldDef[]) => ({
      ...state,
      formBuilderConfig,
    })
  )(this.formBuilderConfigUpdater$);

  private readonly formGroupUpdater = this.updater(
    (state: DeviceFormDataState, value: UntypedFormGroup) => ({
      ...state,
      form: value,
    })
  )(this.formUpdater$);

  readonly setDeviceTypes = this.updater(
    (
      state: DeviceFormDataState,
      deviceTypes: DeviceCategoryType[] | null | undefined
    ) => ({
      ...state,
      deviceTypes: deviceTypes ?? [],
    })
  );

  readonly setBrands = this.updater(
    (state: DeviceFormDataState, brandsState: BrandsState | undefined) => ({
      ...state,
      brands: brandsState ?? ({} as BrandsState),
    })
  );

  readonly setCategory = this.updater(
    (state: DeviceFormDataState, category: string) => ({
      ...state,
      category,
    })
  );

  readonly setInitialFormData = this.updater(
    (
      state: DeviceFormDataState,
      initialFormData: DeviceFormValue | null | undefined
    ) => ({
      ...state,
      initialForm: initialFormData ?? undefined,
    })
  );

  /**
   * VM
   */
  readonly vm$: Observable<ViewModel> = this.select(
    this.formBuilderConfig$,
    this.form$,
    this.disableBrands$,
    (formBuilderConfig, form, disableBrands) => ({
      formBuilderConfig,
      form,
      disableBrands,
    })
  );

  constructor(
    private dynamicFormBuilder: DynamicFormbuilderService,
    private deviceFormConfigService: DeviceFormConfigService
  ) {
    super({
      brands: initialBrandState,
      category: '',
      deviceTypes: [],
      form: undefined,
      formBuilderConfig: undefined,
      initialForm: undefined,
    });
  }
}
